// src/pages/parts/productSearch/components/FloatingBasket.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "primereact/button";
import { useLanguage } from "../../../../contexts/LanguageContext";
import { getCurrencySymbol } from "../../../../utils/currencySymbol";
import {
  useGetBasketQuery,
  useRemoveFromBasketMutation,
  useUpdateProductQuantityInBasketMutation,
} from "../../../../api/endpoints/basket";
import "./FloatingBasket.scss";

const STORAGE_KEY = "floatingBasketCollapsed";

const priceFormatter = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const formatPrice = (value) => priceFormatter.format(Number(value ?? 0));

const FloatingBasket = () => {
  const { th } = useLanguage();
  const navigate = useNavigate();

  const { data } = useGetBasketQuery();
  const basket = data?.data;
  const lines = basket?.lines ?? [];

  const [removeFromBasket, { isLoading: isRemoving }] =
    useRemoveFromBasketMutation();
  const [removingCode, setRemovingCode] = useState(null);

  const [updateProductQuantityInBasket, { isLoading: isUpdatingQty }] =
    useUpdateProductQuantityInBasketMutation();
  const [updatingCode, setUpdatingCode] = useState(null);
  // Optimistik adet gösterimi: sunucu cevabı gelene kadar kullanıcıya anlık yansıtır.
  const [uiQty, setUiQty] = useState({});

  // Servis bülten siparişlerinde adet değişimi kısıtlıdır.
  const isServiceBulletinOrder =
    basket?.bulletNumber != null && basket?.bulletNumber !== "";

  // Başlangıçta kapalı gelsin (kayıtlı tercih varsa ona uy).
  const [collapsed, setCollapsed] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === null ? true : stored === "true";
  });
  const [bump, setBump] = useState(false);

  const initializedRef = useRef(false);
  const prevQtyRef = useRef(0);

  const totalQuantity = useMemo(
    () =>
      basket?.totalQuantity ??
      lines.reduce((acc, x) => acc + Number(x.quantity ?? 0), 0),
    [basket?.totalQuantity, lines]
  );

  // Sepetteki farklı parça (kalem) sayısı.
  const distinctItemCount = lines.length;

  const totalAmount = useMemo(() => {
    if (basket?.netAmount != null) return Number(basket.netAmount);
    if (basket?.totalSubAmount != null) return Number(basket.totalSubAmount);
    return lines.reduce(
      (acc, x) =>
        acc +
        (x.netAmount != null
          ? Number(x.netAmount)
          : Number(x.amount ?? 0) - Number(x.discount ?? 0)),
      0
    );
  }, [basket, lines]);

  // Sepete yeni ürün eklendiğinde paneli otomatik AÇMA; yalnızca hafif bir
  // animasyon ile kullanıcıyı bilgilendir. Panel yalnızca tıklanınca açılır.
  useEffect(() => {
    if (!initializedRef.current) {
      prevQtyRef.current = totalQuantity;
      initializedRef.current = true;
      return;
    }

    if (totalQuantity > prevQtyRef.current) {
      setBump(true);
      const timer = setTimeout(() => setBump(false), 600);
      prevQtyRef.current = totalQuantity;
      return () => clearTimeout(timer);
    }

    prevQtyRef.current = totalQuantity;
  }, [totalQuantity]);

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem(STORAGE_KEY, String(next));
      return next;
    });
  };

  const getShownQty = (line) =>
    uiQty[line.itemCode] ?? Number(line.quantity ?? 1);

  const handleUpdateQuantity = async (line, nextQty) => {
    if (isServiceBulletinOrder) return;
    const desired = Math.max(1, Number(nextQty ?? 1));
    const serverQty = Number(line.quantity ?? 1);
    if (desired === serverQty && uiQty[line.itemCode] == null) return;

    setUiQty((m) => ({ ...m, [line.itemCode]: desired }));
    setUpdatingCode(line.itemCode);
    try {
      await updateProductQuantityInBasket({
        itemCode: line.itemCode,
        quantity: desired,
      }).unwrap();
      setUiQty((m) => {
        const { [line.itemCode]: _ignored, ...rest } = m;
        return rest;
      });
    } catch {
      // Hata mesajı global olarak baseQueryWithReauth tarafından gösteriliyor.
      setUiQty((m) => ({ ...m, [line.itemCode]: serverQty }));
    } finally {
      setUpdatingCode(null);
    }
  };

  const handleRemove = async (itemCode) => {
    if (isRemoving) return;
    setRemovingCode(itemCode);
    try {
      await removeFromBasket(itemCode).unwrap();
    } catch {
      // Hata mesajı global olarak baseQueryWithReauth tarafından gösteriliyor.
    } finally {
      setRemovingCode(null);
    }
  };

  if (lines.length === 0) {
    return null;
  }

  const basketCurrency = getCurrencySymbol(basket?.currency);

  if (collapsed) {
    return (
      <button
        type="button"
        className={`floating-basket-fab${bump ? " floating-basket--bump" : ""}`}
        onClick={toggleCollapsed}
        aria-label={th("floatingBasket.open", { fallback: "Sepeti Aç" })}
      >
        <i className="pi pi-shopping-cart" />
        <span className="floating-basket-fab__badge">{distinctItemCount}</span>
      </button>
    );
  }

  return (
    <div
      className={`floating-basket-panel${bump ? " floating-basket--bump" : ""}`}
    >
      <div className="floating-basket-panel__header">
        <div className="floating-basket-panel__title">
          <i className="pi pi-shopping-cart" />
          <span>{th("floatingBasket.title", { fallback: "Sepetim" })}</span>
          <span className="floating-basket-panel__count">{distinctItemCount}</span>
        </div>
        <button
          type="button"
          className="floating-basket-panel__minimize"
          onClick={toggleCollapsed}
          aria-label={th("floatingBasket.minimize", { fallback: "Küçült" })}
        >
          <i className="pi pi-minus" />
        </button>
      </div>

      <div className="floating-basket-panel__body">
        {lines.map((line) => {
          const lineCurrency = getCurrencySymbol(line.currency);
          const unitPrice = formatPrice(line.unitRetailPrice);

          // Perakende (liste) tutarı ile indirimli (net) tutar.
          const retailTotal = Number(line.totalUnitRetailPrice ?? 0);
          const discountedTotal = Number(
            line.netAmount ??
              line.dealerDiscountedAmount ??
              line.totalUnitRetailPrice ??
              0
          );
          const hasDiscount = retailTotal > discountedTotal + 0.001;

          const shownQty = getShownQty(line);
          const isLineUpdating = isUpdatingQty && updatingCode === line.itemCode;
          const qtyDisabled = isServiceBulletinOrder || isLineUpdating;

          return (
            <div className="floating-basket-item" key={line.itemCode}>
              <div className="floating-basket-item__info">
                <span className="floating-basket-item__code">
                  {line.itemCode}
                </span>
                <span
                  className="floating-basket-item__name"
                  title={line.partName}
                >
                  {line.partName}
                </span>
                <span className="floating-basket-item__meta">
                  {unitPrice} {lineCurrency}{" "}
                  {th("floatingBasket.perPiece", { fallback: "/ adet" })}
                </span>

                <div className="floating-basket-item__stepper">
                  <Button
                    icon="pi pi-minus"
                    rounded
                    text
                    className="floating-basket-item__step"
                    onClick={() => handleUpdateQuantity(line, shownQty - 1)}
                    disabled={qtyDisabled || shownQty <= 1}
                    aria-label={th("floatingBasket.decrease", {
                      fallback: "Azalt",
                    })}
                  />
                  <span className="floating-basket-item__qty">
                    {isLineUpdating ? (
                      <i className="pi pi-spin pi-spinner" />
                    ) : (
                      shownQty
                    )}
                  </span>
                  <Button
                    icon="pi pi-plus"
                    rounded
                    text
                    className="floating-basket-item__step"
                    onClick={() => handleUpdateQuantity(line, shownQty + 1)}
                    disabled={qtyDisabled}
                    aria-label={th("floatingBasket.increase", {
                      fallback: "Arttır",
                    })}
                  />
                </div>
              </div>

              <div className="floating-basket-item__right">
                <div className="floating-basket-item__prices">
                  <span className="floating-basket-item__total">
                    {formatPrice(discountedTotal)} {lineCurrency}
                  </span>
                  {hasDiscount && (
                    <span className="floating-basket-item__retail">
                      <s>
                        {formatPrice(retailTotal)} {lineCurrency}
                      </s>{" "}
                      {th("floatingBasket.retail", { fallback: "perakende" })}
                    </span>
                  )}
                </div>
                <Button
                  icon="pi pi-trash"
                  rounded
                  text
                  severity="danger"
                  className="floating-basket-item__remove"
                  onClick={() => handleRemove(line.itemCode)}
                  loading={isRemoving && removingCode === line.itemCode}
                  disabled={isRemoving}
                  aria-label={th("floatingBasket.remove", {
                    fallback: "Kaldır",
                  })}
                />
              </div>
            </div>
          );
        })}
      </div>

      <div className="floating-basket-panel__footer">
        <div className="floating-basket-panel__total">
          <span>{th("floatingBasket.total", { fallback: "Toplam Tutar" })}</span>
          <strong>
            {formatPrice(totalAmount)} {basketCurrency}
          </strong>
        </div>
        <Button
          label={th("floatingBasket.goToBasket", { fallback: "Sepete Git" })}
          icon="pi pi-arrow-right"
          iconPos="right"
          className="floating-basket-panel__go w-full"
          onClick={() => navigate("/basket")}
        />
      </div>
    </div>
  );
};

export default FloatingBasket;
